import { useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import { GET_BUSINESS_ACCOUNT } from 'store/queries';
import AgentPortalError from 'errors/AgentPortalError';
import { identity } from 'ramda';

const BusinessAccountQuery = ({ children, onError, onAccountFound, onAccountNotFound }) => {
  const client = useApolloClient();
  const [state, setState] = useState({
    loading: false,
    data: null
  });

  const resetQuery = () => {
    setState({
      loading: false,
      data: null
    });
  };

  const runQuery = (variables) => {
    setState({
      ...state,
      loading: true
    });

    return client
      .query({
        query: GET_BUSINESS_ACCOUNT,
        variables
      })
      .then(({ data: { businessAccount } }) => {
        setState({ loading: false, data: businessAccount });

        return onAccountFound(businessAccount);
      })
      .catch((error) => {
        const graphQLErrors = AgentPortalError.flattenGraphQLErrors(error.graphQLErrors);

        const errorHandlers = {
          BUSINESS_ACCOUNT_NOT_FOUND: onAccountNotFound
        };

        const graphQLError = graphQLErrors.find(({ extensions: { code } }) => Object.keys(errorHandlers).includes(code));

        const errorHandler = graphQLError ? errorHandlers[graphQLError.extensions.code] : onError;

        const callOnError = () => (errorHandler ? errorHandler(error) : null);

        // onError called via setState allows ErrorBoundary to catch errors
        setState({ loading: false, data: null });
        callOnError();
      });
  };

  const { loading, data } = state;

  return children(runQuery, { loading, data, resetQuery });
};

BusinessAccountQuery.propTypes = {
  onError: PropTypes.func.isRequired,
  onAccountFound: PropTypes.func,
  onAccountNotFound: PropTypes.func,
  children: PropTypes.func.isRequired
};

BusinessAccountQuery.defaultProps = {
  onAccountFound: identity,
  onAccountNotFound: identity
};

export default BusinessAccountQuery;
